import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import metadataApi from '../api/metadata';
import { getDocumentationPost, getVisibleDocumentationPosts } from '../api/documentation';

const DEFAULT_DOC_ROUTES = new Set(['astroql', 'python']);
const COLUMN_GROUP_SUFFIX_PATTERN = /^[A-Za-z0-9]{1,8}$/;

const columnSignature = (column) => `${column.data_type}|${column.is_nullable ? 'nullable' : 'required'}`;

const formatColumnLine = (column) => {
  const nullability = column.is_nullable ? 'nullable' : 'not null';
  return `${column.name}: ${column.data_type} (${nullability})`;
};

const compactColumns = (columns) => {
  const groupedColumns = new Map();
  const standaloneColumns = [];

  columns.forEach((column) => {
    const parts = column.name.split('_');
    const suffix = parts[parts.length - 1];

    if (parts.length >= 2 && COLUMN_GROUP_SUFFIX_PATTERN.test(suffix)) {
      const prefix = parts.slice(0, -1).join('_');
      const key = `${prefix}|${columnSignature(column)}`;
      const group = groupedColumns.get(key);

      if (group) {
        group.suffixes.push(suffix);
      } else {
        groupedColumns.set(key, {
          prefix,
          data_type: column.data_type,
          is_nullable: column.is_nullable,
          suffixes: [suffix]
        });
      }
      return;
    }

    standaloneColumns.push(column);
  });

  const compacted = [];

  groupedColumns.forEach((group) => {
    if (group.suffixes.length < 3) {
      group.suffixes.forEach((suffix) => {
        compacted.push(formatColumnLine({
          name: `${group.prefix}_${suffix}`,
          data_type: group.data_type,
          is_nullable: group.is_nullable
        }));
      });
      return;
    }

    const nullability = group.is_nullable ? 'nullable' : 'not null';
    const orderedSuffixes = [...group.suffixes].sort((left, right) => left.localeCompare(right));
    compacted.push(`${group.prefix}_[${orderedSuffixes.join(', ')}]: ${group.data_type} (${nullability})`);
  });

  standaloneColumns.forEach((column) => {
    compacted.push(formatColumnLine(column));
  });

  return compacted.sort((left, right) => left.localeCompare(right));
};

const CreatePrompt = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentationPosts, setDocumentationPosts] = useState([]);
  const [fullDocumentationById, setFullDocumentationById] = useState({});
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [copiedDocId, setCopiedDocId] = useState(null);
  const [schemas, setSchemas] = useState([]);
  const [expandedSchemas, setExpandedSchemas] = useState({});
  const [tablesBySchema, setTablesBySchema] = useState({});
  const [columnsByTable, setColumnsByTable] = useState({});
  const [selectedTables, setSelectedTables] = useState([]);
  const [loadingSchema, setLoadingSchema] = useState(null);
  const [copyState, setCopyState] = useState('idle');
  const [promptText, setPromptText] = useState(
    'Inspect one table first, then upload the new table, and finally perform the coordinate match. Return the exact steps or code to run.'
  );
  const selectedDocIdsRef = useRef([]);
  const selectedTablesRef = useRef([]);
  const promptTextRef = useRef(promptText);

  useEffect(() => {
    const loadPageData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [docs, schemaList] = await Promise.all([
          getVisibleDocumentationPosts(),
          metadataApi.getSchemas()
        ]);

        const sortedDocs = [...docs].sort((left, right) => left.order_index - right.order_index);
        setDocumentationPosts(sortedDocs);
        const defaultDocIds = sortedDocs
          .filter((post) => DEFAULT_DOC_ROUTES.has(post.route))
          .map((post) => post.id);
        selectedDocIdsRef.current = defaultDocIds;
        setSelectedDocIds(defaultDocIds);
        setSchemas(schemaList);
      } catch (err) {
        console.error('Failed to load prompt builder data:', err);
        setError('Failed to load documentation or database metadata.');
      } finally {
        setLoading(false);
      }
    };

    loadPageData();
  }, []);

  const getDocRoute = (post) => post.route || `route/${post.id}`;

  const getFullDocumentationPost = async (post) => {
    if (fullDocumentationById[post.id]) {
      return fullDocumentationById[post.id];
    }

    const fullPost = await getDocumentationPost(post.id);
    setFullDocumentationById((prev) => ({
      ...prev,
      [post.id]: fullPost
    }));
    return fullPost;
  };

  const toggleDocSelection = (postId) => {
    setSelectedDocIds((currentIds) => {
      const nextIds = currentIds.includes(postId)
        ? currentIds.filter((id) => id !== postId)
        : [...currentIds, postId];
      selectedDocIdsRef.current = nextIds;
      return nextIds;
    });
  };

  const loadTablesForSchema = async (schemaName) => {
    if (tablesBySchema[schemaName]) {
      return tablesBySchema[schemaName];
    }

    setLoadingSchema(schemaName);
    try {
      const schemaTables = await metadataApi.getTables(schemaName);
      setTablesBySchema((prev) => ({
        ...prev,
        [schemaName]: schemaTables
      }));
      return schemaTables;
    } finally {
      setLoadingSchema(null);
    }
  };

  const loadColumnsForTable = async (schemaName, tableName) => {
    const tableKey = `${schemaName}.${tableName}`;
    if (columnsByTable[tableKey]) {
      return columnsByTable[tableKey];
    }

    const columns = await metadataApi.getColumns(schemaName, tableName);
    setColumnsByTable((prev) => ({
      ...prev,
      [tableKey]: columns
    }));
    return columns;
  };

  const toggleSchemaExpanded = async (schemaName) => {
    const nextExpanded = !expandedSchemas[schemaName];
    setExpandedSchemas((prev) => ({
      ...prev,
      [schemaName]: nextExpanded
    }));

    if (nextExpanded) {
      try {
        await loadTablesForSchema(schemaName);
      } catch (err) {
        console.error(`Failed to load tables for schema ${schemaName}:`, err);
      }
    }
  };

  const toggleTableSelection = async (schemaName, tableName) => {
    const tableKey = `${schemaName}.${tableName}`;
    const isSelected = selectedTables.includes(tableKey);

    if (!isSelected) {
      try {
        await loadColumnsForTable(schemaName, tableName);
      } catch (err) {
        console.error(`Failed to load columns for ${tableKey}:`, err);
      }
    }

    setSelectedTables((currentTables) => {
      const nextTables = isSelected
        ? currentTables.filter((currentKey) => currentKey !== tableKey)
        : [...currentTables, tableKey];
      selectedTablesRef.current = nextTables;
      return nextTables;
    });
  };

  const handleCopyDoc = async (post) => {
    try {
      const fullPost = await getFullDocumentationPost(post);
      const payload = [
        `# ${fullPost.title}`,
        `route: /documentation/${getDocRoute(fullPost)}`,
        '',
        fullPost.content.trim()
      ].join('\n');
      await navigator.clipboard.writeText(payload);
      setCopiedDocId(post.id);
      window.setTimeout(() => {
        setCopiedDocId((currentId) => (currentId === post.id ? null : currentId));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy documentation entry:', err);
    }
  };

  const handleCopyPrompt = async () => {
    setCopyState('working');

    try {
      const selectedDocIdsSnapshot = selectedDocIdsRef.current;
      const selectedTablesSnapshot = selectedTablesRef.current;
      const selectedDocs = await Promise.all(
        documentationPosts
          .filter((post) => selectedDocIdsSnapshot.includes(post.id))
          .map((post) => getFullDocumentationPost(post))
      );
      const selectedTableDetails = await Promise.all(
        selectedTablesSnapshot.map(async (tableKey) => {
          const [schemaName, tableName] = tableKey.split('.');
          const columns = await loadColumnsForTable(schemaName, tableName);
          return { schemaName, tableName, columns };
        })
      );

      const promptPayload = [
        'You are reading context selected from the Astronomy Data Portal.',
        'Use the documentation excerpts and database table summaries below as the primary reference.',
        'If the request asks for AstroQL, ADQL, SQL, or Python, stay consistent with the selected docs.',
        'When using the database tables, respect the available column names exactly as provided.',
        '',
        'Task for the LLM:',
        promptTextRef.current.trim() || 'Help me using the selected documentation and tables.',
        '',
        'Selected documentation:',
        selectedDocs.length > 0
          ? selectedDocs.map((post) => [
              `## ${post.title}`,
              `route: /documentation/${getDocRoute(post)}`,
              '',
              post.content.trim()
            ].join('\n')).join('\n\n')
          : 'None selected.',
        '',
        'Selected database tables:',
        selectedTableDetails.length > 0
          ? selectedTableDetails.map(({ schemaName, tableName, columns }) => [
              `## ${schemaName}.${tableName}`,
              ...compactColumns(columns).map((line) => `- ${line}`)
            ].join('\n')).join('\n\n')
          : 'None selected.'
      ].join('\n');

      await navigator.clipboard.writeText(promptPayload);
      setCopyState('copied');
      window.setTimeout(() => {
        setCopyState((currentState) => (currentState === 'copied' ? 'idle' : currentState));
      }, 2000);
    } catch (err) {
      console.error('Failed to build prompt payload:', err);
      setCopyState('idle');
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card p-8 shadow-lg">
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400"></div>
            <span>Loading prompt builder...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-status-error-light-text/20 dark:border-status-error-dark-text/20 bg-status-error-light-bg dark:bg-status-error-dark-bg p-6 text-status-error-light-text dark:text-status-error-dark-text">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card p-8 shadow-lg">
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
                Create Prompt
              </h1>
              <p className="mt-2 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                Write the request, pick docs and tables, then copy.
              </p>
            </div>
            <button
              type="button"
              onClick={handleCopyPrompt}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600"
            >
              {copyState === 'copied' ? 'Prompt Copied' : copyState === 'working' ? 'Building...' : 'Copy Prompt'}
            </button>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.35fr_0.9fr]">
            <div className="rounded-2xl border border-ui-light-border dark:border-ui-dark-border bg-ui-light-background dark:bg-ui-dark-background p-5">
              <div className="mb-3">
                <h2 className="text-xl font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary">
                  Prompt
                </h2>
                <p className="mt-1 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                  Keep it short and explicit.
                </p>
              </div>

              <textarea
                value={promptText}
                onChange={(event) => {
                  promptTextRef.current = event.target.value;
                  setPromptText(event.target.value);
                }}
                rows={8}
                className="w-full rounded-xl border border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card p-4 text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30"
                placeholder="Describe exactly what you want the LLM to do."
              />
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-primary-200 bg-primary-50 p-5 dark:border-primary-800 dark:bg-primary-900/20">
                <div className="text-xs font-medium uppercase tracking-wide text-primary-700 dark:text-primary-300">
                  Example
                </div>
                <p className="mt-2 text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary">
                  Give me a script that first inspects one table, then uploads another table, and then runs a coordinate match between them.
                </p>
              </div>

              <div className="rounded-2xl border border-ui-light-border dark:border-ui-dark-border bg-ui-light-background dark:bg-ui-dark-background p-5">
                <div className="text-xs font-medium uppercase tracking-wide text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                  Output
                </div>
                <p className="mt-2 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                  One prompt to paste into your LLM, with your request, selected docs, and selected tables.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[1.05fr_1fr]">
        <section className="rounded-2xl border border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card p-6 shadow-lg">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary">
              Documentation
            </h2>
            <p className="mt-1 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
              Select docs to include.
            </p>
          </div>

          <div className="space-y-3">
            {documentationPosts.map((post) => {
              const isSelected = selectedDocIds.includes(post.id);
              return (
                <div
                  key={post.id}
                  className={`rounded-xl border p-4 transition ${
                    isSelected
                      ? 'border-primary-400 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20'
                      : 'border-ui-light-border dark:border-ui-dark-border bg-ui-light-background dark:bg-ui-dark-background'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <label className="flex min-w-0 cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleDocSelection(post.id)}
                        className="mt-1 h-4 w-4 rounded border-ui-light-border dark:border-ui-dark-border text-primary-600 focus:ring-primary-500"
                      />
                      <div className="min-w-0">
                        <div className="font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                          {post.title}
                        </div>
                        <div className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                          /documentation/{getDocRoute(post)}
                        </div>
                      </div>
                    </label>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleCopyDoc(post)}
                        className="rounded-md border border-ui-light-border dark:border-ui-dark-border px-3 py-1.5 text-xs font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover"
                      >
                        {copiedDocId === post.id ? 'Copied' : 'Copy'}
                      </button>
                      <Link
                        to={`/documentation/${getDocRoute(post)}`}
                        className="rounded-md border border-ui-light-border dark:border-ui-dark-border px-3 py-1.5 text-xs font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover"
                      >
                        Open
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="rounded-2xl border border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card p-6 shadow-lg">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary">
              Database Tables
            </h2>
            <p className="mt-1 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
              Select tables to include.
            </p>
          </div>

          <div className="max-h-[640px] space-y-3 overflow-auto pr-1">
            {schemas.map((schemaName) => {
              const schemaTables = tablesBySchema[schemaName] || [];
              const isExpanded = expandedSchemas[schemaName];
              const isLoadingSchema = loadingSchema === schemaName;

              return (
                <div
                  key={schemaName}
                  className="overflow-hidden rounded-xl border border-ui-light-border dark:border-ui-dark-border bg-ui-light-background dark:bg-ui-dark-background"
                >
                  <button
                    type="button"
                    onClick={() => toggleSchemaExpanded(schemaName)}
                    className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover"
                  >
                    <div>
                      <div className="font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                        {schemaName}
                      </div>
                      <div className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                        {schemaTables.length > 0 ? `${schemaTables.length} tables loaded` : 'Click to load tables'}
                      </div>
                    </div>
                    <div className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                      {isLoadingSchema ? 'Loading...' : isExpanded ? 'Hide' : 'Show'}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-ui-light-border dark:border-ui-dark-border">
                      {schemaTables.length === 0 && !isLoadingSchema ? (
                        <div className="px-4 py-4 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                          No tables available in this schema.
                        </div>
                      ) : (
                        <div className="divide-y divide-ui-light-border dark:divide-ui-dark-border">
                          {schemaTables.map((tableName) => {
                            const tableKey = `${schemaName}.${tableName}`;
                            const isSelected = selectedTables.includes(tableKey);

                            return (
                              <label
                                key={tableKey}
                                className={`flex cursor-pointer items-center justify-between gap-3 px-4 py-3 transition ${
                                  isSelected
                                    ? 'bg-primary-50 dark:bg-primary-900/20'
                                    : 'hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover'
                                }`}
                              >
                                <div className="flex min-w-0 items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleTableSelection(schemaName, tableName)}
                                    className="h-4 w-4 rounded border-ui-light-border dark:border-ui-dark-border text-primary-600 focus:ring-primary-500"
                                  />
                                  <div className="min-w-0">
                                    <div className="truncate text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                                      {tableName}
                                    </div>
                                    <div className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                                      {schemaName}.{tableName}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                                  {columnsByTable[tableKey] ? `${columnsByTable[tableKey].length} cols` : 'load on select'}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>

    </div>
  );
};

export default CreatePrompt;
